import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiErrorDetail } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  // If a valid session cookie is already present, send straight to dashboard.
  useEffect(() => {
    let alive = true;
    api.get('/auth/me')
      .then(() => { if (alive) navigate('/admin', { replace: true }); })
      .catch(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/auth/admin/login', { email, password });
      // No client-side token to store — the server set an httpOnly cookie.
      navigate('/admin', { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || 'Invalid email or password');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-vl-ink/55">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-vl-bg2 flex items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        autoComplete="off"
        className="w-full max-w-md rounded-3xl bg-vl-surface border hairline p-8 space-y-6"
      >
        <div>
          <p className="mono-label text-vl-violet">Valanti Labs · Admin</p>
          <h1 className="font-serif-display text-3xl text-vl-ink mt-2">Sign in</h1>
          <p className="text-sm text-vl-ink/65 mt-1">For internal Valanti Labs team use only.</p>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-vl-ink mb-2">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={ADMIN.email}
            autoComplete="email"
            required
            className="w-full rounded-xl border border-vl-ink/15 bg-transparent px-4 py-3 text-vl-ink focus:outline-none focus:border-vl-violet focus:ring-1 focus:ring-vl-violet"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-vl-ink mb-2">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={ADMIN.password}
            autoComplete="current-password"
            required
            className="w-full rounded-xl border border-vl-ink/15 bg-transparent px-4 py-3 text-vl-ink focus:outline-none focus:border-vl-violet focus:ring-1 focus:ring-vl-violet"
          />
        </label>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid={ADMIN.submit}
          className="w-full inline-flex items-center justify-center rounded-full bg-vl-ink text-vl-bg px-6 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors disabled:opacity-50"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
